import React from 'react';
import PropTypes from 'prop-types';
import { compose, withHandlers, withState } from 'recompose';
import { Button, Text } from 'components/atoms';
import { ChevronDown } from 'components/svg';
import { Image } from 'components/core/Image';
import IconDeals from 'assets/images/search/icon-deals.svg';
import checkWhite from 'assets/images/icons/check-white.svg';

import { DEALS_FILTER_OPTIONS } from 'config/dealFilter';

import './DealsFilter.scss';

export const enhance = compose(
	withState('isOpen', 'setIsOpen', false),
	withHandlers({
		handleChange: (props) => (selValue) => {
			const { setIsOpen, onChange } = props;
			setIsOpen(false);
			if (onChange) {
				onChange(selValue);
			}
		},
		handleClick: (props) => () => {
			const { setIsOpen } = props;
			setIsOpen((isOpen) => !isOpen);
		},
		closeDropdown: (props) => () => {
			const { setIsOpen } = props;
			setIsOpen(false);
		},
	}),
);

export function DealsFilter({
	value,
	handleChange,
	handleClick,
	isOpen,
	closeDropdown,
}) {
	const containerRef = React.useRef(null);
	const selectedOption =
		DEALS_FILTER_OPTIONS.find((opt) => opt.value === value) ||
		DEALS_FILTER_OPTIONS[0];

	React.useEffect(() => {
		function handleClickOutside(event) {
			if (
				containerRef.current &&
				!containerRef.current.contains(event.target)
			) {
				closeDropdown();
			}
		}

		function handleCloseOnScrollOrResize() {
			closeDropdown();
		}

		if (isOpen) {
			document.addEventListener('mousedown', handleClickOutside);
			window.addEventListener(
				'scroll',
				handleCloseOnScrollOrResize,
				true,
			);
			window.addEventListener('resize', handleCloseOnScrollOrResize);
		}

		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
			window.removeEventListener(
				'scroll',
				handleCloseOnScrollOrResize,
				true,
			);
			window.removeEventListener('resize', handleCloseOnScrollOrResize);
		};
	}, [isOpen, closeDropdown]);

	return (
		<div
			className="deals-filter-container"
			ref={containerRef}
		>
			<Button
				className="deals-filter-selected"
				onClick={handleClick}
				aria-expanded={isOpen}
				aria-haspopup="listbox"
				aria-label="Filter deals options"
				tabIndex={0}
			>
				<Text
					color="white"
					weight="extraBold"
				>
					<img
						src={IconDeals}
						alt="icon-deals"
					/>
					{selectedOption.label}
				</Text>
				<ChevronDown />
			</Button>
			{isOpen && (
				<ul
					className="deals-filter-options"
					role="listbox"
					aria-label="Filter deals options list"
				>
					<hr />
					{DEALS_FILTER_OPTIONS.map((option) => (
						<li
							className="option"
							key={option.value}
							role="option"
							aria-selected={
								selectedOption.value === option.value
							}
						>
							<Button
								className={
									selectedOption.value === option.value
										? 'option-button active'
										: 'option-button'
								}
								onClick={() => handleChange(option.value)}
							>
								{option.label}
								{selectedOption.value === option.value && (
									<Image
										src={checkWhite}
										className="check-icon"
									/>
								)}
							</Button>
						</li>
					))}
				</ul>
			)}
		</div>
	);
}

DealsFilter.propTypes = {
	value: PropTypes.oneOf(['all', 'fmf', 'additional']),
	handleChange: PropTypes.func,
	handleClick: PropTypes.func,
	isOpen: PropTypes.bool,
	closeDropdown: PropTypes.func,
	onChange: PropTypes.func,
};

DealsFilter.defaultProps = {
	value: 'all',
};

export default enhance(DealsFilter);
